import { EventListeners, MinzeElement, Reactive } from 'minze'

import { Font, MODALS, SCREEN_WIDTHS } from '../../../constants'
import { AdditionalTypes } from '../../../modules/additionalTypes'
import { Callback } from '../../../modules/callback'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import {
  AbstractSearchCore,
  AbstractSubscriber,
  IFacet,
  IFacetOption,
  ISearchConfig,
} from '../../../modules/search'
import { getVisibleWidth } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'disciplinesSearch', {
  disciplines: 'Disciplines',
  empty: 'Filtrez par discipline',
  mobileLabel: 'Disciplines',
  applyButton: 'Appliquer',
})

Locale.getInstance().registerKeySet('en', 'disciplinesSearch', {
  disciplines: 'Disciplines',
  empty: 'Filter by discipline',
  mobileLabel: 'Disciplines',
  applyButton: 'Apply',
})

export interface LvcDisciplinesSearch {
  selectedCategories: string[]
  subcategories: any[]
}

class LvcDisciplinesSubscriber extends AbstractSubscriber {
  private _updateCallback: (data: any) => void
  private _core: AbstractSearchCore | null = null
  constructor(groupId: string, updateCallback: (data: any) => void) {
    super(groupId)
    this._updateCallback = updateCallback
  }

  async attached(core: AbstractSearchCore): Promise<void> {
    this._core = core
  }

  detaching(): void {
    this._core = null
  }

  public async setDisciplines(disciplines: { value: string; label: string }[]): Promise<void> {
    let searchConfig: ISearchConfig | undefined = this._core?.searchConfig
    if (!searchConfig) {
      return
    }

    let originalFacet: IFacet | undefined = searchConfig.facets?.find(
      (facet: IFacet) => facet.name === 'uiAdditionalTypesCode'
    )
    if (originalFacet) {
      originalFacet.options = []
    } else {
      originalFacet = {
        name: 'uiAdditionalTypesCode',
        options: [],
      }

      searchConfig.facets?.push(originalFacet)
    }

    // get the label of the disciplines from their code

    for (let k in disciplines) {
      originalFacet?.options?.push({
        value: disciplines[k].value,
        extraData: disciplines[k].label, // there was already a extraData field in the IFacetOption interface, so I used it
        selected: true,
        available: true,
      })
    }

    if (searchConfig) {
      await this._core?.applySearchConfig(searchConfig)
    }
  }

  async onSearchConfigUpdate(data: any): Promise<void> {
    this._updateCallback(data)
  }

  async onSearchTextUpdate(): Promise<void> {}

  async onFacetUpdate(): Promise<void> {}

  async onResultUpdate(): Promise<void> {}
}

export class LvcDisciplinesSearch extends MinzeElement {
  private clickCb: string = Callback.attach(this.onToggleButtonClick.bind(this))
  private clearCb: string = Callback.attach(this.onClearButtonClick.bind(this))
  private toggleCb: string = Callback.attach(this.onToggle.bind(this))
  private drawerToggleCb = Callback.attach(this.onDrawerToggle.bind(this))
  private drawerClickCb = Callback.attach(this.onDrawerToggle.bind(this))
  isOpen: boolean = false
  reactive: Reactive = [
    ['selectedCategories', []],
    ['additionalTypes', {}],
    ['list', []],
  ]
  additionalTypes: any = {}
  list: any[] = []
  lvcDisciplinesSubscriber: LvcDisciplinesSubscriber | null = null

  onToggleButtonClick(): void {
    MainModule.getInstance().showModal(MODALS.DISCIPLINES_DROPDOWN)
  }

  onClearButtonClick(): void {
    this.clearFilter(new Event('click'))
  }

  onToggle(state: boolean): void {
    this.isOpen = state
    this.rerender()
  }

  onDrawerToggle(): void {
    this.isOpen = !this.isOpen
    this.rerender()
  }

  onDrawerClick(): void {
    this.isOpen = !this.isOpen
    this.rerender()
  }

  openDisciplinesDropdown = () => {
    MainModule.getInstance().showModal(MODALS.DISCIPLINES_DROPDOWN)
  }

  onCategoryClick = (e: Event) => {
    const target = e.target as HTMLElement
    const chip = target.closest('.category-chip') as HTMLElement

    if (chip) {
      const value = chip.getAttribute('value')
      if (this.selectedCategories.includes(value as string)) {
        let idx = this.selectedCategories.indexOf(value as string)
        this.selectedCategories.splice(idx, 1)
      } else {
        this.selectedCategories.push(value as string)
      }

      const lang = MainModule.getInstance().getLocale().getLanguage()

      // Map selected values to their corresponding labels
      const selectedDisciplines = this.selectedCategories.map(value => {
        const discipline = this.list.find(item => item.code === value)
        return {
          value: value,
          label: discipline && lang === 'fr' ? discipline.labelFr : discipline.labelEn || '',
        }
      })

      this.lvcDisciplinesSubscriber?.setDisciplines(selectedDisciplines)
    }
  }

  // Map selected values to their corresponding labels
  mapSelectedDisciplines() {
    const selectedDisciplines = this.selectedCategories.map(value => {
      const discipline = this.list.find(item => item.value === value)
      return {
        value: value,
        label: discipline ? discipline.label : '',
      }
    })
  }

  closeModal = () => {
    MainModule.getInstance().hideModal(MODALS.DISCIPLINES_DROPDOWN)
  }

  async onReady(): Promise<void> {
    this.additionalTypes = await AdditionalTypes.getInstance().getAllInTree(true)

    this.list = await AdditionalTypes.getInstance().getAll()
    this.lvcDisciplinesSubscriber = new LvcDisciplinesSubscriber(
      this.dataGroupId as string,
      this.handleSearchUpdate.bind(this)
    )
    MainModule.getInstance().getSearchManager().subscribe(this.lvcDisciplinesSubscriber)

    this.rerender()
  }

  async handleSearchUpdate(data: any): Promise<void> {
    if (this.selectedCategories.length === 0) {
      let facet = data.facets.find((facet: IFacet) => facet.name === 'uiAdditionalTypesCode')

      if (facet) {
        facet.options
          .filter((option: IFacetOption) => option.selected)
          .forEach((option: IFacetOption) => {
            if (!this.selectedCategories.includes(option.value)) {
              this.selectedCategories.push(option.value)
            }
          })
      }
    }
  }

  renderContent = () => {
    // let renderedSubcategories = [];
    //
    // for (let k in this.additionalTypes as any) {
    //   let category: any = this.additionalTypes[k] as any;
    //   if (category?.children?.length === 0) {
    //     continue;
    //   }
    //   renderedSubcategories.push(
    //     this.renderCategories(category.label, category.children),
    //   );
    // }

    return this.renderCategories(
      Locale.getKey('disciplinesSearch.disciplines'),
      this.additionalTypes as any[]
    )
  }

  renderDisciplineLabel() {
    let count = this.selectedCategories.length
    if (count == 0) {
      return Locale.getKey('disciplinesSearch.empty')
    } else if (count > 1) {
      return `${this.getDisciplineLabel(this.selectedCategories[0])} + ${count - 1}`
    } else {
      return this.getDisciplineLabel(this.selectedCategories[0])
    }
  }

  getDisciplineLabel(id: string) {
    let item = this.list.find((item: any) => item.code == id)

    return item ? item.label : ''
  }

  clearFilter = (event: Event) => {
    event.preventDefault()
    this.selectedCategories.splice(0, this.selectedCategories.length)

    // Map selected values to their corresponding labels
    const selectedDisciplines = this.selectedCategories.map(value => {
      const discipline = this.list.find(item => item.value === value)
      return {
        value: value,
        label: discipline ? discipline.label : '',
      }
    })

    this.lvcDisciplinesSubscriber?.setDisciplines(selectedDisciplines)
    this.isOpen = false
    this.rerender()
    MainModule.getInstance().hideModal(MODALS.DISCIPLINES_DROPDOWN)
  }

  getTextValue = () => {
    if (!this.selectedCategories || !this.selectedCategories.length) return
    return this.selectedCategories.map(category => this.getDisciplineLabel(category)).join(', ')
  }

  renderCategories = (label: string, categories: any[]) => {
    let renderedCategories = []
    for (let k in categories) {
      let category = categories[k]
      renderedCategories.push(
        `
          <lvc-filter-chip
            value="${category.code}"
            class="category-chip"
            selected="${this.selectedCategories.includes(category.code)}"
          >
            ${category.label}
          </lvc-filter-chip>
        `
      )
    }

    return `
      <lvc-box padding="${window.matchMedia('(max-width: 730px)').matches ? '0' : '14px'}">
        <div class="header">
          <h4 class="disciplines-subtitle">
            ${label}
            ${
              this.selectedCategories.length
                ? `
              <div class="counter">${this.selectedCategories.length}</div>
            `
                : ''
            }
          </h4>
        </div>
        <lvc-box
          id="disciplines-list"
          display="flex"
          flexDirection="column"
          rowGap="15px"
          height="fit-content"
        >
          ${renderedCategories.join('')}
        </lvc-box>
      </lvc-box>
    `
  }

  renderDesktop() {
    return /* html */ `
    <lvc-dropdown
      on-toggle="${this.toggleCb}"
      modal-id=${MODALS.DISCIPLINES_DROPDOWN}
      flex-grow="1"
      fullscreen="true"
    >
      <lvc-search-filter-button
        slot="lvc-dropdown-trigger"
        count="${this.selectedCategories.length}"
        ${this.isOpen ? 'selected' : ''}
        on-click="${this.clickCb}"
        on-clear="${this.clearCb}"
        data-value="${this.getTextValue()}"
      >
        ${Locale.getKey('disciplinesSearch.disciplines')}
      </lvc-search-filter-button>
      <div
      class="lvc-dropdown-content"
      slot="lvc-dropdown-content">
        <div class="lvc-dropdown-scrollable">
          ${this.renderContent()}
        </div>
      </div>
    </lvc-dropdown>
  `
  }

  renderMobile() {
    return /* html */ `
      <lvc-search-filter-mobile
        on-toggle="${this.drawerToggleCb}"
        title="${Locale.getKey('disciplinesSearch.disciplines')}"
        data-count="${this.selectedCategories.length}"
        ${this.isOpen ? `open="true"` : ''}
      >
        <lvc-search-filter-button
          slot="lvc-drawer-trigger"
          count="${this.selectedCategories.length}"
          on-click="${this.drawerClickCb}"
          on-clear="${this.clearCb}"
          data-value="${this.getTextValue()}"
        >
          ${Locale.getKey('disciplinesSearch.disciplines')}
        </lvc-search-filter-button>
        <div class="drawer-content"
        slot="lvc-drawer-content">
          ${this.renderContent()}
        </div>
      </lvc-search-filter-mobile>
      `
  }

  html = () => {
    if (getVisibleWidth() < SCREEN_WIDTHS.DESKTOP_MEDIUM) {
      return this.renderMobile()
    } else {
      return this.renderDesktop()
    }
  }

  css = () => `
    h4 {
      margin: 0 0 4px 0;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      opacity: 0.3;
    }
    
    .header {
      padding-bottom: 20px;
      margin-bottom: 20px;
      border-bottom: 1px solid #1E007D4D;
    }

    .disciplines-subtitle {
      font-weight: ${Font.weight.bold};
      font-size: 16px;
      line-height: 150%;
      color: #1E007D;
      opacity: 1;
      display: flex;
      align-items: center;
      width: fit-content;
      position: relative;
    }
    
    .disciplines-subtitle .counter {
      border: 1px solid #1E007D;
      width: 30px;
      height: 30px;
      font-size: 12px;
      font-weight: 400;
      border-radius: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      right: -6px;
      bottom: 6px;
      transform: translateX(100%);
    }
    
    #disciplines-list {
      max-height: 300px;
      overflow-y: auto;
    }
    
    .category-chip:not(:last-child) {
      padding-bottom: 10px;
      border-bottom: 1px solid #BFF0FF;
    }
    
    @media screen and (max-width: 991px) {

      .header {
        display: none;
      }

      #disciplines-list {
        max-height: 100%;
        overflow-y: auto;
      }

      .lvc-dropdown-content {
        max-height: calc(100vh - 72px - 73px - 84.5px - 10px);
        max-width: 100vw;
        overflow-y: scroll;
      }
    }
  `

  onDestory() {
    Callback.detach(this.clickCb)
    Callback.detach(this.clearCb)
    Callback.detach(this.toggleCb)
    Callback.detach(this.drawerClickCb)
    Callback.detach(this.drawerToggleCb)
  }

  eventListeners: EventListeners = [
    ['#btn-disciplines-search', 'click', this.openDisciplinesDropdown.bind(this)],
    ['lvc-clear-filter-button', 'click', this.clearFilter.bind(this)],
    ['.category-chip', 'click', this.onCategoryClick.bind(this)],
    ['lvc-back-icon', 'click', this.closeModal.bind(this)],
    [window, 'lvc:clear-search', this.clearFilter.bind(this)],
  ]
}
